// Renders a legal document from the generated block list in src/data/legal.
// Reuses the AuthPage shell (AuthBg background + fixed Navbar) so the page
// feels like the rest of the app.
import React, { useMemo } from 'react';
import HomepageBackground from '../../assets/backgrounds/AuthBg.png';
import Navbar from '../../components/Auth/Navbar';
import styles from './LegalDocument.module.css';

// Acronyms and proper nouns that keep their casing when section titles are
// converted from the documents' all-caps headings to sentence case.
const PRESERVED_TERMS = new Map(
    ['AI', 'SP-API', 'API', 'Amazon', 'Google', 'EcomBuddha'].map((term) => [term.toUpperCase(), term])
);

const caseWord = (word) => PRESERVED_TERMS.get(word.toUpperCase()) ?? word.toLowerCase();

// "5. GOOGLE USER DATA AND SIGN-IN" -> "5. Google user data and sign-in"
const toSentenceCase = (text) => {
    const [, numbering, rest] = text.match(/^((?:\d+\.?\s*)?)(.*)$/);
    const cased = rest.replace(/[A-Za-z]+(?:-[A-Za-z]+)*/g, (token) =>
        // Try the whole hyphenated compound first ("SP-API"), then each part
        // separately so "AI-GENERATED" becomes "AI-generated".
        PRESERVED_TERMS.get(token.toUpperCase()) ?? token.split('-').map(caseWord).join('-')
    );
    return numbering + cased.charAt(0).toUpperCase() + cased.slice(1);
};

const renderSpans = (spans) =>
    spans.map((span, index) => {
        const content = span.i ? <em>{span.text}</em> : span.text;
        return span.b
            ? <strong key={index}>{content}</strong>
            : <React.Fragment key={index}>{content}</React.Fragment>;
    });

// Consecutive list blocks share a single <ul>/<ol> wrapper.
const groupBlocks = (blocks) =>
    blocks.reduce((acc, block) => {
        const previous = acc[acc.length - 1];
        if (block.type === 'li' && previous?.type === 'list' && previous.list === block.list) {
            previous.items.push(block);
        } else if (block.type === 'li') {
            acc.push({ type: 'list', list: block.list, items: [block] });
        } else {
            acc.push(block);
        }
        return acc;
    }, []);

const renderBlock = (block, index) => {
    switch (block.type) {
        case 'h1':
        case 'h2':
        case 'h3': {
            const Heading = block.type === 'h1' ? 'h2' : block.type === 'h2' ? 'h3' : 'h4';
            return (
                <Heading key={index} id={block.id} className={styles[block.type]}>
                    {block.text}
                </Heading>
            );
        }
        case 'list': {
            const List = block.list === 'ol' ? 'ol' : 'ul';
            return (
                <List key={index} className={styles.list}>
                    {block.items.map((item, itemIndex) => (
                        <li key={itemIndex}>{renderSpans(item.spans)}</li>
                    ))}
                </List>
            );
        }
        case 'table': {
            const [header, ...rows] = block.rows;
            return (
                <div key={index} className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>{header.map((cell, cellIndex) => <th key={cellIndex}>{cell}</th>)}</tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }
        default:
            return <p key={index} className={styles.paragraph}>{renderSpans(block.spans)}</p>;
    }
};

const LegalDocument = ({ document: doc }) => {
    const { meta, blocks } = doc;
    const grouped = useMemo(() => groupBlocks(blocks), [blocks]);
    // Titles are cased here rather than in the markup so the transform runs
    // once per document instead of on every render.
    const contents = useMemo(
        () => blocks
            .filter((block) => block.type === 'h1' && block.id)
            .map(({ id, text }) => ({ id, label: toSentenceCase(text) })),
        [blocks]
    );

    return (
        <div className={styles.page} style={{ backgroundImage: `url(${HomepageBackground})` }}>
            <Navbar hideSignUp={true} />
            <header className={styles.header}>
                <h1 className={styles.title}>{meta.title}</h1>
                <p className={styles.entity}>{meta.entity}</p>
                <p className={styles.meta}>
                    Effective {meta.effectiveDate} &nbsp;|&nbsp; Version {meta.version}
                </p>
            </header>

            <div className={styles.body}>
                <nav className={styles.contents} aria-label="Contents">
                    <p className={styles.contentsTitle}>Contents</p>
                    <ol className={styles.contentsList}>
                        {contents.map(({ id, label }) => (
                            <li key={id}>
                                <a href={`#${id}`}>{label}</a>
                            </li>
                        ))}
                    </ol>
                </nav>

                <article className={styles.content}>{grouped.map(renderBlock)}</article>
            </div>
        </div>
    );
};

export default LegalDocument;
